import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Avatar,
  IconButton,
  useDisclosure,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Grid,
  GridItem,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
  Flex,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
} from "@chakra-ui/react";
import {
  FiSearch,
  FiEye,
  FiUser,
  FiBriefcase,
  FiFileText,
  FiPlus,
  FiFilter,
  FiEdit,
  FiTrash2,
  FiDownload,
  FiMoreVertical,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { employeeService } from "../employeeService";
import { digitalFilesService } from "../digitalFilesService";
import EmployeeProfileModal from "./EmployeeProfileModal";

const EmployeeDatabase = () => {
  console.log("EmployeeDatabase component rendered");
  const navigate = useNavigate();

  // Dark mode colors
  const textColor = useColorModeValue("gray.800", "gray.100");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const searchIconColor = useColorModeValue("gray.500", "gray.400");
  const bgColor = useColorModeValue("white", "gray.800");
  const cardBg = useColorModeValue("white", "gray.750");
  const tableBg = useColorModeValue("white", "gray.800");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  // Responsive breakpoint
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const cancelRef = useRef();
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    departments: 0,
    completeProfiles: 0,
  });

  const {
    isOpen: isProfileOpen,
    onOpen: onProfileOpen,
    onClose: onProfileClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchTerm, departmentFilter, statusFilter]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      console.log("Fetching employees...");
      const response = await employeeService.getAllEmployees();
      console.log("Employee response:", response);
      const employeeData = response || [];
      console.log("Employee data:", employeeData);

      // Fetch digital files status for each employee
      const employeesWithProfileStatus = await Promise.all(
        employeeData.map(async (employee) => {
          try {
            const [personalInfo, jobHistory, documents] = await Promise.all([
              digitalFilesService.personalInfo
                .getPersonalInfo(employee.employeeId)
                .catch(() => null),
              digitalFilesService.jobHistory
                .getJobHistory(employee.employeeId)
                .catch(() => []),
              digitalFilesService.documents
                .getDocuments(employee.employeeId)
                .catch(() => []),
            ]);

            const hasPersonalInfo = !!personalInfo?.data;
            const hasJobHistory =
              Array.isArray(jobHistory?.data) && jobHistory.data.length > 0;
            const hasDocuments =
              Array.isArray(documents?.data) && documents.data.length > 0;

            return {
              ...employee,
              profileStatus: {
                personalInfo: hasPersonalInfo,
                jobHistory: hasJobHistory,
                documents: hasDocuments,
                completeness:
                  ([hasPersonalInfo, hasJobHistory, hasDocuments].filter(
                    Boolean,
                  ).length /
                    3) *
                  100,
              },
            };
          } catch (error) {
            return {
              ...employee,
              profileStatus: {
                personalInfo: false,
                jobHistory: false,
                documents: false,
                completeness: 0,
              },
            };
          }
        }),
      );

      setEmployees(employeesWithProfileStatus);
      calculateStats(employeesWithProfileStatus);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast({
        title: "Error loading employees",
        description: "Could not load employee data",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (employeeData) => {
    const total = employeeData.length;
    const active = employeeData.filter((emp) => emp.status === "Active").length;
    const departments = new Set(employeeData.map((emp) => emp.department)).size;
    const completeProfiles = employeeData.filter(
      (emp) => emp.profileStatus?.completeness === 100,
    ).length;

    setStats({
      totalEmployees: total,
      activeEmployees: active,
      departments,
      completeProfiles,
    });
  };

  const filterEmployees = () => {
    let filtered = employees;

    if (searchTerm) {
      filtered = filtered.filter(
        (employee) =>
          employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.employeeId
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          employee.email?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (departmentFilter) {
      filtered = filtered.filter(
        (employee) => employee.department === departmentFilter,
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(
        (employee) => employee.status === statusFilter,
      );
    }

    setFilteredEmployees(filtered);
  };

  const handleViewProfile = (employee) => {
    setSelectedEmployee(employee);
    onProfileOpen();
  };

  const handleProfileUpdate = () => {
    fetchEmployees(); // Refresh data after profile updates
  };

  const handleCreateEmployee = () => {
    navigate("/employees/create");
  };

  const handleEditEmployee = (employee) => {
    console.log("🔧 Edit employee clicked:", employee);
    console.log("🔧 Navigating to:", `/employees/${employee._id}/edit`);
    navigate(`/employees/${employee._id}/edit`);
  };

  const handleFormSuccess = () => {
    fetchEmployees();
    // The modal will auto-close in the EmployeeFormPage after redirect
  };

  const handleDeleteEmployee = (employee) => {
    setEmployeeToDelete(employee);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    if (!employeeToDelete) return;

    setIsDeleting(true);
    try {
      await employeeService.deleteEmployee(employeeToDelete._id);
      toast({
        title: "Employee deleted",
        description: `${employeeToDelete.name} has been removed from the database`,
        status: "success",
        duration: 3000,
      });
      fetchEmployees();
      onDeleteClose();
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error deleting employee",
        description:
          error.response?.data?.message ||
          error.message ||
          "Could not delete employee",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
      setEmployeeToDelete(null);
    }
  };

  const handleExportData = () => {
    if (filteredEmployees.length === 0) {
      toast({
        title: "No data to export",
        description: "There are no employees to export",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    const csvData = filteredEmployees.map((emp) => ({
      ID: emp.employeeId,
      Name: emp.name,
      Email: emp.email,
      Department: emp.department || "N/A",
      designation: emp.designation || "N/A",
      Status: emp.status,
      ProfileCompleteness: `${Math.round(
        emp.profileStatus?.completeness || 0,
      )}%`,
    }));

    // Simple CSV export
    const headers = Object.keys(csvData[0]).join(",");
    const rows = csvData.map((row) => Object.values(row).join(","));
    const csv = [headers, ...rows].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `employees_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Data exported",
      description: `Exported ${filteredEmployees.length} employee records to CSV`,
      status: "success",
      duration: 2000,
    });
  };

  const getDepartments = () => {
    return [...new Set(employees.map((emp) => emp.department).filter(Boolean))];
  };

  const getProfileCompletenessColor = (percentage) => {
    if (percentage === 100) return "green";
    if (percentage >= 66) return "yellow";
    if (percentage >= 33) return "orange";
    return "red";
  };

  const getProfileCompletenessText = (percentage) => {
    if (percentage === 100) return "Complete";
    if (percentage >= 66) return "Mostly Complete";
    if (percentage >= 33) return "Partial";
    return "Incomplete";
  };

  if (loading) {
    return (
      <Box textAlign="center" py={12}>
        <Spinner size="xl" color="blue.500" mb={4} />
        <Text>Loading employee database...</Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
          <Box flex={1} minW={{ base: "full", md: "auto" }}>
            <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
              Employee Database
            </Text>
            <Text
              color={mutedColor}
              fontSize={{ base: "sm", md: "md" }}
              display={{ base: "none", md: "block" }}
            >
              Comprehensive employee profiles and digital files
            </Text>
          </Box>
          {/* Desktop Actions */}
          <HStack spacing={3} display={{ base: "none", md: "flex" }}>
            <Button
              leftIcon={<FiDownload />}
              variant="outline"
              colorScheme="blue"
              onClick={handleExportData}
              isDisabled={filteredEmployees.length === 0}
            >
              Export
            </Button>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={handleCreateEmployee}
            >
              Add Employee
            </Button>
          </HStack>
          {/* Mobile Menu */}
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<FiMoreVertical />}
              variant="ghost"
              display={{ base: "flex", md: "none" }}
            />
            <MenuList>
              <MenuItem
                icon={<FiDownload />}
                onClick={handleExportData}
                isDisabled={filteredEmployees.length === 0}
              >
                Export Data
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>

        {/* Statistics Cards */}
        {/* Desktop: Grid Layout */}
        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={4}
          display={{ base: "none", md: "grid" }}
        >
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Total Employees</StatLabel>
                <StatNumber>{stats.totalEmployees}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Active Employees</StatLabel>
                <StatNumber>{stats.activeEmployees}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Departments</StatLabel>
                <StatNumber>{stats.departments}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Complete Profiles</StatLabel>
                <StatNumber>{stats.completeProfiles}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </Grid>

        {/* Mobile: Horizontal Scrollable Metrics */}
        <Flex
          display={{ base: "flex", md: "none" }}
          overflowX="auto"
          gap={3}
          pb={2}
          css={{
            "&::-webkit-scrollbar": {
              height: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#CBD5E0",
              borderRadius: "2px",
            },
          }}
        >
          <Card
            bg={cardBg}
            borderColor={borderColor}
            minW="140px"
            cursor="pointer"
            onClick={() => {
              setStatusFilter("");
              setDepartmentFilter("");
            }}
            _active={{ transform: "scale(0.98)" }}
          >
            <CardBody p={4}>
              <Text fontSize="xs" color={mutedColor} fontWeight="medium">
                Total
              </Text>
              <Text fontSize="2xl" fontWeight="bold" mt={1}>
                {stats.totalEmployees}
              </Text>
            </CardBody>
          </Card>
          <Card
            bg={cardBg}
            borderColor={borderColor}
            minW="140px"
            cursor="pointer"
            onClick={() => {
              setStatusFilter("Active");
            }}
            _active={{ transform: "scale(0.98)" }}
          >
            <CardBody p={4}>
              <Text fontSize="xs" color={mutedColor} fontWeight="medium">
                Active
              </Text>
              <Text fontSize="2xl" fontWeight="bold" mt={1}>
                {stats.activeEmployees}
              </Text>
            </CardBody>
          </Card>
          <Card
            bg={cardBg}
            borderColor={borderColor}
            minW="140px"
            cursor="pointer"
            _active={{ transform: "scale(0.98)" }}
          >
            <CardBody p={4}>
              <Text fontSize="xs" color={mutedColor} fontWeight="medium">
                Departments
              </Text>
              <Text fontSize="2xl" fontWeight="bold" mt={1}>
                {stats.departments}
              </Text>
            </CardBody>
          </Card>
          <Card
            bg={cardBg}
            borderColor={borderColor}
            minW="140px"
            cursor="pointer"
            _active={{ transform: "scale(0.98)" }}
          >
            <CardBody p={4}>
              <Text fontSize="xs" color={mutedColor} fontWeight="medium">
                Complete
              </Text>
              <Text fontSize="2xl" fontWeight="bold" mt={1}>
                {stats.completeProfiles}
              </Text>
            </CardBody>
          </Card>
        </Flex>

        {/* Filters */}
        <Card bg={cardBg} borderColor={borderColor}>
          <CardBody p={{ base: 3, md: 4 }}>
            {/* Mobile: Two Row Layout */}
            <VStack spacing={3} display={{ base: "flex", md: "none" }}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FiSearch color={searchIconColor} />
                </InputLeftElement>
                <Input
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="md"
                />
              </InputGroup>
              <HStack spacing={2} w="full">
                <Select
                  placeholder="Department"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  size="md"
                  flex={1}
                >
                  {getDepartments().map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </Select>
                <Select
                  placeholder="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  size="md"
                  flex={1}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="On Leave">On Leave</option>
                </Select>
                {(searchTerm || departmentFilter || statusFilter) && (
                  <IconButton
                    icon={<FiFilter />}
                    variant="outline"
                    onClick={() => {
                      setSearchTerm("");
                      setDepartmentFilter("");
                      setStatusFilter("");
                    }}
                    aria-label="Clear filters"
                    size="md"
                  />
                )}
              </HStack>
            </VStack>

            {/* Desktop: Single Row Layout */}
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
              gap={4}
              display={{ base: "none", md: "grid" }}
            >
              <Box>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color={searchIconColor} />
                  </InputLeftElement>
                  <Input
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Box>
              <Box>
                <Select
                  placeholder="All Departments"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  {getDepartments().map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Select
                  placeholder="All Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="On Leave">On Leave</option>
                </Select>
              </Box>
              <Box>
                <Button
                  leftIcon={<FiFilter />}
                  variant="outline"
                  onClick={() => {
                    setSearchTerm("");
                    setDepartmentFilter("");
                    setStatusFilter("");
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </CardBody>
        </Card>

        {/* Desktop: Employee Table */}
        <Card
          bg={tableBg}
          borderColor={borderColor}
          display={{ base: "none", md: "block" }}
        >
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Employee</Th>
                  <Th>Department</Th>
                  <Th>designation</Th>
                  <Th>Status</Th>
                  <Th>Profile Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredEmployees.map((employee) => (
                  <Tr
                    key={employee._id}
                    _hover={{ bg: hoverBg }}
                    transition="background 0.2s"
                  >
                    <Td>
                      <HStack spacing={3}>
                        <Avatar name={employee.name} size="sm" />
                        <Box>
                          <Text fontWeight="medium">{employee.name}</Text>
                          <Text fontSize="sm" color={mutedColor}>
                            ID: {employee.employeeId}
                          </Text>
                        </Box>
                      </HStack>
                    </Td>
                    <Td>{employee.department || "Not assigned"}</Td>
                    <Td>{employee.designation || "Not assigned"}</Td>
                    <Td>
                      <Badge
                        colorScheme={
                          employee.status === "Active"
                            ? "green"
                            : employee.status === "Inactive"
                              ? "red"
                              : "orange"
                        }
                      >
                        {employee.status}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Badge
                          colorScheme={getProfileCompletenessColor(
                            employee.profileStatus?.completeness || 0,
                          )}
                          variant="subtle"
                        >
                          {getProfileCompletenessText(
                            employee.profileStatus?.completeness || 0,
                          )}
                        </Badge>
                        <Text fontSize="xs" color={mutedColor}>
                          {Math.round(
                            employee.profileStatus?.completeness || 0,
                          )}
                          % complete
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<FiEye />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleViewProfile(employee)}
                          title="View Profile"
                        />
                        <IconButton
                          icon={<FiEdit />}
                          size="sm"
                          variant="ghost"
                          colorScheme="green"
                          onClick={() => handleEditEmployee(employee)}
                          title="Edit Employee"
                        />
                        <IconButton
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteEmployee(employee)}
                          title="Delete Employee"
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            {filteredEmployees.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color={mutedColor}>
                  {employees.length === 0
                    ? "No employees found"
                    : "No employees match your filters"}
                </Text>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Mobile: Employee Card List */}
        <VStack
          spacing={3}
          display={{ base: "flex", md: "none" }}
          align="stretch"
        >
          {filteredEmployees.map((employee) => (
            <Card
              key={employee._id}
              bg={cardBg}
              borderColor={borderColor}
              borderWidth="1px"
              _active={{ transform: "scale(0.99)" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <VStack spacing={3} align="stretch">
                  {/* Top Section: Avatar + Name + Status */}
                  <HStack justify="space-between" align="start">
                    <HStack spacing={3} flex={1}>
                      <Avatar name={employee.name} size="md" />
                      <Box flex={1}>
                        <Text fontWeight="semibold" fontSize="md">
                          {employee.name}
                        </Text>
                        <Text fontSize="sm" color={mutedColor}>
                          ID: {employee.employeeId}
                        </Text>
                      </Box>
                    </HStack>
                    <Badge
                      colorScheme={
                        employee.status === "Active"
                          ? "green"
                          : employee.status === "Inactive"
                            ? "red"
                            : "orange"
                      }
                      fontSize="xs"
                      px={2}
                      py={1}
                    >
                      {employee.status}
                    </Badge>
                  </HStack>

                  <Divider />

                  {/* Details Section */}
                  <VStack spacing={2} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={mutedColor}>
                        Department:
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {employee.department || "Not assigned"}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={mutedColor}>
                        designation:
                      </Text>
                      <Text fontSize="sm" fontWeight="medium">
                        {employee.designation || "Not assigned"}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color={mutedColor}>
                        Profile:
                      </Text>
                      <HStack>
                        <Badge
                          colorScheme={getProfileCompletenessColor(
                            employee.profileStatus?.completeness || 0,
                          )}
                          variant="subtle"
                          fontSize="xs"
                        >
                          {Math.round(
                            employee.profileStatus?.completeness || 0,
                          )}
                          %
                        </Badge>
                        <Text fontSize="xs" color={mutedColor}>
                          {getProfileCompletenessText(
                            employee.profileStatus?.completeness || 0,
                          )}
                        </Text>
                      </HStack>
                    </HStack>
                  </VStack>

                  <Divider />

                  {/* Actions Section */}
                  <HStack spacing={2} justify="stretch">
                    <Button
                      leftIcon={<FiEye />}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => handleViewProfile(employee)}
                      flex={1}
                      minH="44px"
                    >
                      View
                    </Button>
                    <IconButton
                      icon={<FiEdit />}
                      size="sm"
                      variant="ghost"
                      colorScheme="green"
                      onClick={() => handleEditEmployee(employee)}
                      aria-label="Edit"
                      minH="44px"
                      minW="44px"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleDeleteEmployee(employee)}
                      aria-label="Delete"
                      minH="44px"
                      minW="44px"
                    />
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          ))}

          {filteredEmployees.length === 0 && (
            <Box textAlign="center" py={8}>
              <Text color={mutedColor}>
                {employees.length === 0
                  ? "No employees found"
                  : "No employees match your filters"}
              </Text>
            </Box>
          )}
        </VStack>

        {/* Mobile: Floating Add Button */}
        <IconButton
          icon={<FiPlus />}
          colorScheme="blue"
          size="lg"
          designation="fixed"
          bottom={6}
          right={6}
          borderRadius="full"
          boxShadow="lg"
          onClick={handleCreateEmployee}
          display={{ base: "flex", md: "none" }}
          zIndex={10}
          minH="56px"
          minW="56px"
          aria-label="Add Employee"
        />
      </VStack>

      {/* Employee Profile Modal for View */}
      {selectedEmployee && (
        <EmployeeProfileModal
          isOpen={isProfileOpen}
          onClose={onProfileClose}
          employee={selectedEmployee}
          onDataUpdate={handleProfileUpdate}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Employee
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete{" "}
              <strong>{employeeToDelete?.name}</strong>? This action cannot be
              undone and will permanently remove all employee data.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                ml={3}
                isLoading={isDeleting}
                loadingText="Deleting..."
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default EmployeeDatabase;
